import React, { useEffect, useMemo, useState } from 'react';
import { Button, Input, Modal, Select, Space, Switch, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetContractDetails } from '../../hooks/genericHooks';
import type { VwToContractDetails } from '../../types';

export type ContractSelectValue = {
  contract_id: number;
  reference_id: string;
  label: string;
  contract: VwToContractDetails;
};

type Props = {
  open: boolean;
  onCancel: () => void;
  onOk: (value: ContractSelectValue) => void;
  value?: unknown;
  options?: unknown[];
  title?: string;
};

const includesText = (value: unknown, query: string) =>
  String(value ?? '').toLowerCase().includes(query.toLowerCase());

const buildContractLabel = (contract: VwToContractDetails): string => {
  const reference = String(contract.reference_id ?? '').trim();
  const name = String(contract.contract_name ?? '').trim();
  if (reference && name) return `${reference} — ${name}`;
  return reference || name || `ID ${contract.id}`;
};

const readSelectedId = (value: unknown): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) return Number(value);
  if (typeof value === 'object') {
    const record = value as { contract_id?: unknown; id?: unknown; contract?: { id?: unknown } };
    const raw = record.contract_id ?? record.contract?.id ?? record.id;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  }
  return null;
};

const readReference = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const reference = value.trim();
    return reference || null;
  }
  if (value && typeof value === 'object' && 'reference_id' in value) {
    const reference = String((value as { reference_id?: unknown }).reference_id ?? '').trim();
    return reference || null;
  }
  return null;
};

const ContractSelect: React.FC<Props> = ({ open, onCancel, onOk, value, title }) => {
  const { data, isLoading } = useGetContractDetails();
  const contracts: VwToContractDetails[] = data?.data ?? [];

  const [query, setQuery] = useState('');
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(() => readSelectedId(value));
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const id = readSelectedId(value);
    if (id != null && contracts.some((contract) => Number(contract.id) === id)) {
      setSelectedId(id);
      return;
    }
    const reference = readReference(value);
    const match = reference
      ? contracts.find((contract) => String(contract.reference_id ?? '').trim() === reference)
      : undefined;
    setSelectedId(match ? Number(match.id) : null);
    if (open && match?.status === 'inactive') setShowInactive(true);
  }, [value, open, contracts]);

  const matchesQuery = (contract: VwToContractDetails, tokens: string[]) => {
    if (!tokens.length) return true;
    return tokens.every((token) => (
      includesText(contract.id, token)
      || includesText(contract.reference_id, token)
      || includesText(contract.contract_name, token)
      || includesText(contract.contract_type, token)
      || includesText(contract.payment_type, token)
      || includesText(contract.status, token)
      || includesText(contract.description, token)
    ));
  };

  const afterStatus = useMemo(
    () => contracts.filter((contract) => showInactive || contract.status !== 'inactive'),
    [contracts, showInactive],
  );

  const afterSearch = useMemo(() => {
    const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
    return afterStatus.filter((contract) => matchesQuery(contract, tokens));
  }, [afterStatus, query]);

  const companyOptions = useMemo(() => {
    const names = new Set<string>();
    afterSearch.forEach((contract) => {
      const name = String(contract.company_name ?? '').trim();
      if (name) names.add(name);
    });
    return Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map((name) => ({ value: name, label: name }));
  }, [afterSearch]);

  const filtered = useMemo(() => {
    if (!companyName) return afterSearch;
    return afterSearch.filter((contract) => String(contract.company_name ?? '').trim() === companyName);
  }, [afterSearch, companyName]);

  useEffect(() => {
    if (companyName && !companyOptions.some((option) => option.value === companyName)) {
      setCompanyName(null);
    }
  }, [companyName, companyOptions]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, companyName, showInactive]);

  const selectedRow = useMemo(
    () => filtered.find((contract) => Number(contract.id) === Number(selectedId)) ?? null,
    [filtered, selectedId],
  );

  const confirm = (contract: VwToContractDetails) => {
    onOk({
      contract_id: Number(contract.id),
      reference_id: String(contract.reference_id ?? ''),
      label: buildContractLabel(contract),
      contract,
    });
  };

  const tightCell = {
    onCell: () => ({ style: { padding: '2px 8px', border: 'none', lineHeight: '18px' } }),
    onHeaderCell: () => ({ style: { padding: '4px 8px', border: 'none' } }),
  };
  const columns: ColumnsType<VwToContractDetails> = [
    { title: 'Reference', dataIndex: 'reference_id', key: 'reference_id', width: 100, ellipsis: true, ...tightCell },
    { title: 'Contract', dataIndex: 'contract_name', key: 'contract_name', width: 200, ellipsis: true, ...tightCell },
    { title: 'Company', dataIndex: 'company_name', key: 'company_name', width: 160, ellipsis: true, ...tightCell },
    { title: 'Start', dataIndex: 'start_date', key: 'start_date', width: 80, ...tightCell },
    { title: 'End', dataIndex: 'end_date', key: 'end_date', width: 80, ...tightCell },
  ];

  return (
    <Modal
      open={open}
      title={title || 'Select Contract'}
      onCancel={onCancel}
      footer={null}
      width={860}
      zIndex={4100}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Space style={{ width: '100%' }} size={8} wrap>
          <Input
            allowClear
            value={query}
            placeholder="Search by reference, name, type, status..."
            onChange={(event) => setQuery(event.target.value)}
            style={{ width: 280 }}
          />
          <Select
            allowClear
            showSearch
            placeholder="Company"
            value={companyName ?? undefined}
            options={companyOptions}
            onChange={(next) => setCompanyName(next ?? null)}
            optionFilterProp="label"
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            style={{ width: 200 }}
          />
          <Space size={6}>
            <Switch checked={showInactive} onChange={setShowInactive} />
            <span>Show inactive</span>
          </Space>
          <Button onClick={onCancel}>Cancel</Button>
          <Button type="primary" disabled={!selectedRow} onClick={() => selectedRow && confirm(selectedRow)}>
            Select
          </Button>
        </Space>
        <Table<VwToContractDetails>
          size="small"
          rowKey={(record) => String(record.id)}
          loading={isLoading}
          dataSource={filtered}
          columns={columns}
          scroll={{ x: 780 }}
          pagination={{
            current: currentPage,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: () => `Filtered: ${filtered.length} / Total: ${contracts.length}`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
          onRow={(record) => ({
            onClick: () => setSelectedId(Number(record.id)),
            onDoubleClick: () => confirm(record),
            style: {
              cursor: 'pointer',
              background: selectedId != null && Number(record.id) === selectedId ? '#e6f7ff' : undefined,
            },
          })}
        />
      </Space>
    </Modal>
  );
};

export default ContractSelect;
